import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaUsers, FaCheckCircle, FaTimes, FaEye, FaSearch,
  FaClock, FaBuilding, FaEnvelope, FaPhone, FaUser,
  FaFileAlt, FaFilter, FaArrowLeft, FaSpinner,
  FaDollarSign, FaBank, FaCalendarAlt, FaBriefcase,
  FaIdCard, FaMapMarkerAlt, FaUniversity
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function AdminClientApproval({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [user, setUser] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/clients/pending`);
      const data = await response.json();
      if (data.success) {
        setClients(data.clients || []);
        setFilteredClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      toast.error('Failed to load client applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = clients;
    
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.registration_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(c => c.status === filterStatus);
    }
    
    setFilteredClients(filtered);
  }, [searchTerm, filterStatus, clients]);

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'active': 'bg-green-100 text-green-600',
      'inactive': 'bg-gray-100 text-gray-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'active': 'Active',
      'inactive': 'Inactive',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  const getClientTypeBadge = (type) => {
    const styles = {
      'prepaid': 'bg-blue-100 text-blue-600',
      'credit': 'bg-purple-100 text-purple-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[type] || 'bg-gray-100 text-gray-600'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R 0.00';
    return `R ${parseFloat(amount).toFixed(2)}`;
  };

  const pendingCount = clients.filter(c => c.status === 'pending').length;

  if (loading) {
    return (
      <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading client applications...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="clients" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <Link to="/clients" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaUsers className="inline mr-2" style={{ color: '#00A86B' }} />
            Client Approvals
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {pendingCount} pending applications • {clients.length} total
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm text-gray-600">Status:</span>
          </div>
              <div className="flex items-center gap-2">
                <FaEye className="text-blue-600" size={12} />
                <span className="text-sm text-gray-600">Click row to review</span>
              </div>
              {/* View-only users can see but not approve */}
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'active' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Active
          </button>
          <button
            onClick={() => setFilterStatus('rejected')}
            className={`px-3 py-1 rounded-full text-sm ${filterStatus === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Rejected
          </button>
          <div className="flex-1"></div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-green w-64"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Applications Found</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {searchTerm ? 'No applications match your search.' : 'All client applications have been reviewed.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClients.map((client) => (
            <div key={client.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>{client.company_name}</h3>
                    <span className={getStatusBadge(client.status)}>
                      {getStatusLabel(client.status)}
                    </span>
                    <span className={getClientTypeBadge(client.client_type)}>
                      {client.client_type || 'prepaid'}
                    </span>
                    {client.client_type === 'credit' && (
                      <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                        Requested: {formatCurrency(client.requested_credit_limit)}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm" style={{ color: '#4A5568' }}>
                    <span className="flex items-center gap-1">
                      <FaUser size={14} /> {client.contact_person}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaEnvelope size={14} /> {client.contact_email || client.email || 'N/A'}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaPhone size={14} /> {client.contact_phone || client.phone || 'N/A'}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaBuilding size={14} /> Reg: {client.registration_number || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                    Submitted: {formatDate(client.created_at)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* ✅ ONLY "Review" button - No quick approve/reject */}
                  <Link
                    to={`/admin/client-approval/${client.id}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ backgroundColor: '#00A86B', color: 'white' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                  >
                    <FaEye size={14} /> Review
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminClientApproval;